import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ExportIcon, InfoIcon, PlayIcon, SearchIcon, TransferIcon, XIcon } from '@shopify/polaris-icons';
import { CategorySelect, MultiSelect } from './Selects';
import { Ic, StatusPill } from '../ui';
import { Modal } from '../ItAssets/common';
import { api } from './apiClient';

// The V2 (guideline rules) and AI (expected revenue) suggestions, and the three-way compare. The page owns the V1 report;
// everything here reads the same Lightspeed cache and creates transfers through the same dialog.

export const V2_DEFAULTS = { strategy: 'footwear', days: 60, zones: [], currentSeason: '', seasons: [], categoryIds: [], skuSearch: '' };
export const AI_DEFAULTS = { days: 60, perTransfer: 25, perUnit: 2, perUnit100mi: 1.5, payback: 2, cooldownDays: 30, categoryIds: [], skuSearch: '' };
const PAGE = 100;
const MAX_LINES = 500;           // the transfers endpoint takes at most this many lines at once
const STRATEGIES = [['footwear', 'Footwear rebalancing'], ['jersey', 'Jersey rebalancing'], ['consolidate', 'Footwear consolidation'], ['jersey_consolidate', 'Jersey consolidation']];
const HELP = {
  footwear: 'Builds complete size runs (5+ sizes) in the stores that sell the category and price tier best. A receiver under 5 sizes is only filled if it can reach 5; a donor with 5 or more sizes keeps them.',
  jersey: 'A store keeps 2 of a size; the rest goes to stores with none, then one.',
  consolidate: 'Moves older footwear, and newer footwear a store has had 6 months without selling, from the other 37 stores into the 9 hubs, within each hub’s capacity. A hub that sold none of a style in 6 months sends it to the warehouse instead. Classics are left out.',
  jersey_consolidate: 'Moves older jerseys, and this year’s jerseys a store has had 6 months without selling, from the other 37 stores to their hub, only enough to bring the hub to 15 of that size; the rest goes to the next hub under 15, then the warehouse. A hub that sold none of a style in 6 months sends it to the warehouse.',
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const num = (n) => Number(n || 0).toLocaleString();
const money = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
export const rowKey = (r) => `${r.item_id}:${r.src_id}:${r.dst_id}`;
const voteKey = (itemId, src, dst) => `${itemId}:${src}:${dst || ''}`;

// Agree / disagree on one line. Either asks for a typed reason before it saves; it then lands on the Inventory Movement Knowledge Feedback tab.
function Vote({ r, votes, onVote }) {
  const v = votes[voteKey(r.item_id, r.src_id, r.dst_id)];
  return (
    <span className="uh-imk-vote">
      {[['agree', CheckIcon, 'Agree'], ['disagree', XIcon, 'Disagree']].map(([k, icon, label]) => (
        <button key={k} type="button" className={`${k}${v?.verdict === k ? ' on' : ''}`} aria-pressed={v?.verdict === k}
          title={v?.verdict === k ? `You said: ${v.reason}` : `${label} with this line`} aria-label={`${label}: ${r.name}`} onClick={() => onVote(r, k)}><Ic source={icon} sm /></button>))}
    </span>
  );
}

function VoteDialog({ vote, prev, onClose, onSave }) {
  const { r, verdict } = vote;
  const [reason, setReason] = useState(prev?.verdict === verdict ? prev.reason : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => { setBusy(true); setError(''); try { await onSave(reason.trim()); } catch (e) { setError(e.message); setBusy(false); } };
  return (
    <Modal title={verdict === 'agree' ? 'Why is this line right?' : 'What is wrong with this line?'} subtitle={`${r.qty} × ${r.name} · ${r.src} → ${r.dst || 'warehouse'}`} focusFirst onClose={onClose}
      footer={<><span className="uh-sp" /><button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="uh-btn primary" disabled={busy || !reason.trim()} onClick={save}>Save feedback</button></>}>
      {error && <div className="uh-alert" role="alert"><div>{error}</div></div>}
      <label className="uh-ia-fld"><span className="lab">Reason<span className="req"> *</span></span>
        <textarea className="uh-inp" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000}
          placeholder="Say it the way you'd tell a buyer, e.g. Keller never sells youth turf, their clubs all play on grass." />
        <span className="uh-ia-hint">Required. It goes to Inventory Movement Knowledge, where it can become guidance for the rules.</span></label>
    </Modal>
  );
}
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function downloadCsv(name, head, lines) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([[head.map(csvCell).join(','), ...lines.map((l) => l.map(csvCell).join(','))].join('\n')], { type: 'text/csv' }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

// One engine's controls, last result and selection. `call` is the API function; `run()` uses the controls as they are now.
export function useEngine(call, toBody, defaults) {
  const [f, setF] = useState(defaults);
  const [result, setResult] = useState(null);
  const [used, setUsed] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const seq = useRef(0);
  const run = useCallback(async (controls = f) => {
    const mine = ++seq.current;
    setRunning(true); setError('');
    try {
      const r = await call(toBody(controls));
      if (mine !== seq.current) return null;
      setResult(r); setUsed(controls); setSelected(new Set());
      return r;
    } catch (e) {
      if (mine === seq.current) setError(e.message);
      return null;
    } finally {
      if (mine === seq.current) setRunning(false);
    }
  }, [call, toBody, f]);
  return { f, set: (patch) => setF((p) => ({ ...p, ...patch })), reset: () => setF(defaults), result, used, running, error, run, selected, setSelected };
}

export const v2Body = (f) => ({ strategy: f.strategy, days: f.days, zones: f.zones, current_season: f.currentSeason, seasons: f.seasons, category_ids: f.categoryIds, sku_search: f.skuSearch });
export const aiBody = (f) => ({ days: f.days, per_transfer: f.perTransfer, per_unit: f.perUnit, per_unit_100mi: f.perUnit100mi, payback: f.payback, cooldown_days: f.cooldownDays,
  category_ids: f.categoryIds, sku_search: f.skuSearch });

function Alert({ tone = '', children }) {
  return <div className={`uh-alert ${tone}`}><Ic source={InfoIcon} sm /><div>{children}</div></div>;
}

const Stat = ({ label, value }) => <div className="uh-stat"><span className="v">{value}</span><span className="k">{label}</span></div>;

function Pager({ page, setPage, total }) {
  const pages = Math.max(1, Math.ceil(total / PAGE));
  if (pages < 2) return null;
  return (
    <span className="uh-ir-pager">
      <button type="button" className="uh-btn slim" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
      <span className="uh-muted">{(page - 1) * PAGE + 1}–{Math.min(page * PAGE, total)} of {num(total)}</span>
      <button type="button" className="uh-btn slim" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
    </span>
  );
}

function ActionBar({ chosen, onClear, onCreate, total, onSelectAll, allSelected }) {
  if (!chosen.length) return null;
  return (
    <div className="uh-ir-abar">
      <b>{plural(chosen.length, 'line')} selected</b>
      <span className="uh-ir-abar-s">{plural(new Set(chosen.map((r) => `${r.src_id}>${r.dst_id}`)).size, 'transfer document')} in Lightspeed</span>
      <span className="uh-sp" />
      {!allSelected && total > chosen.length && <button type="button" className="uh-btn ghost" onClick={onSelectAll}>Select all {num(total)} matching</button>}
      <button type="button" className="uh-btn ghost" onClick={onClear}>Clear</button>
      {chosen.length > MAX_LINES && <span className="uh-help">Create {MAX_LINES} lines at a time. Clear some to continue.</span>}
      <button type="button" className="uh-btn primary" disabled={chosen.length > MAX_LINES} onClick={onCreate}><Ic source={TransferIcon} sm />Create in Lightspeed…</button>
    </div>
  );
}

const passPill = (r, consolidate) => (consolidate
  ? <StatusPill tone={r.pass === 1 ? 'ful' : 'hold'}>{r.pass === 1 ? 'Own hub' : 'Next hub'}</StatusPill>
  : <StatusPill tone={r.pass === 1 ? 'ful' : 'hold'}>{r.pass === 1 ? 'Pass 1 · in region' : 'Pass 2 · neighbouring region'}</StatusPill>);

// ── V2 ───────────────────────────────────────────────────────────────────────

export function V2Panel({ meta, engines, eng, onCreate }) {
  const { f, set, result, used, running, error, run, selected, setSelected } = eng;
  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [votes, setVotes] = useState({});
  const [voting, setVoting] = useState(null);
  const reportId = result?.report_id;
  useEffect(() => {
    setVotes({});
    if (reportId) api.reportFeedback(reportId).then((d) => setVotes(Object.fromEntries(d.feedback.map((x) => [voteKey(x.item_id, x.src_shop_id, x.dest_shop_id), x])))).catch(() => {});
  }, [reportId]);
  const saveVote = async (reason) => {
    const { r, verdict } = voting;
    await api.feedback({ report_id: reportId, engine: 'v2', strategy: used.strategy, item_id: r.item_id, sku: r.sku, item_name: r.name, src_shop_id: r.src_id, src_name: r.src,
      dest_shop_id: r.dst_id || '', dest_name: r.dst || '', qty: r.qty, why: r.why || '', verdict, reason });
    setVotes((p) => ({ ...p, [voteKey(r.item_id, r.src_id, r.dst_id)]: { verdict, reason } }));
    setVoting(null);
  };
  const consolidate = ['consolidate', 'jersey_consolidate'].includes((used || f).strategy);
  const footwearish = ['footwear', 'consolidate'].includes(f.strategy);        // footwear-only controls (the seasons list)
  const hasLeftovers = f.strategy !== 'jersey';                                  // warehouse recommendations exist for everything but jersey rebalancing
  const zoneOptions = (engines?.zones || []).map((z) => ({ value: z, label: z }));
  const seasonOptions = (engines?.seasons || []).map((s) => ({ value: s, label: s }));

  const match = useCallback((r) => { const t = search.trim().toLowerCase(); return !t || `${r.sku} ${r.name} ${r.src} ${r.dst || ''}`.toLowerCase().includes(t); }, [search]);
  const moves = useMemo(() => (result ? result.moves.filter(match) : []), [result, match]);
  const shown = useMemo(() => (view === '1' ? moves.filter((r) => r.pass === 1) : view === '2' ? moves.filter((r) => r.pass === 2) : moves), [moves, view]);
  const leftovers = useMemo(() => (result ? result.leftovers.filter(match) : []), [result, match]);
  const showingLeftovers = view === 'wh';
  const list = showingLeftovers ? leftovers : shown;
  const paged = list.slice((page - 1) * PAGE, page * PAGE);
  const chosen = result ? result.moves.filter((r) => selected.has(rowKey(r))) : [];
  const toggle = (k) => setSelected((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allPageSelected = paged.length > 0 && paged.every((r) => selected.has(rowKey(r)));
  const togglePage = () => setSelected((prev) => { const n = new Set(prev); paged.forEach((r) => (allPageSelected ? n.delete(rowKey(r)) : n.add(rowKey(r)))); return n; });

  const create = () => onCreate({
    lines: chosen.map((r) => ({ src: r.src_id, dest: r.dst_id, item_id: r.item_id, qty: r.qty })),
    labels: Object.fromEntries(chosen.map((r) => [r.item_id, { product: r.name, sku: r.sku }])), reportId: result.report_id, onDone: () => setSelected(new Set()),
  });
  const exportCsv = () => downloadCsv(`rebalancing-v2-${used.strategy}.csv`, ['Pass', 'Item', 'SKU', 'Size', 'From', 'From region', 'To', 'Qty', 'Retail value', 'Why'],
    [...result.moves.map((r) => [r.pass, r.name, r.sku, r.size, r.src, r.src_region, r.dst, r.qty, r.value, r.why]),
      ...result.leftovers.map((r) => ['warehouse', r.name, r.sku, r.size, r.src, r.src_region, 'Recommend warehouse / clearance', r.qty, r.value, r.why])]);

  const s = result?.summary;
  const cross = result ? result.moves.filter((r) => r.pass === 2).reduce((n, r) => n + r.qty, 0) : 0;
  const hubFull = result?.hubs ? result.hubs : null;
  const views = consolidate
    ? [['all', 'All'], ['1', 'To own hub'], ['2', 'To next hub'], ['wh', 'Warehouse recommendations']]
    : [['all', 'All moves'], ['1', 'Pass 1 · in region'], ['2', 'Pass 2 · neighbouring region'], ...(hasLeftovers ? [['wh', 'Warehouse recommendations']] : [])];

  return (
    <>
      <div className="uh-card uh-card-b uh-form-card">
        <div className="uh-note"><Ic source={InfoIcon} sm />
          <div>V2 follows the new guidelines. Choosing a zone looks at every region in it; each store’s own region is tried first, and neighbouring regions only for what is left.
            Category and SKU filters only limit what is shown. The rules always run on the whole region and zone.</div></div>
        <div className="uh-fld"><label>Strategy</label>
          <div className="uh-ir-seg" role="group" aria-label="Strategy">
            {STRATEGIES.map(([k, label]) => <button key={k} type="button" aria-pressed={f.strategy === k} onClick={() => { set({ strategy: k }); setView('all'); }}>{label}</button>)}
          </div></div>
        <div className="uh-grid2">
          {!['consolidate', 'jersey_consolidate'].includes(f.strategy) && <div className="uh-fld"><label htmlFor="v2-days">Analysis period</label>
            <select id="v2-days" className="uh-inp" value={f.days} onChange={(e) => set({ days: Number(e.target.value) })}>
              <option value={30}>Last 30 days</option><option value={60}>Last 60 days</option><option value={90}>Last 90 days</option></select></div>}
          <div className="uh-fld"><label htmlFor="v2-zone">Zones <span className="optl">(blank = all)</span></label>
            <MultiSelect id="v2-zone" noun="zones" options={zoneOptions} value={f.zones} onChange={(v) => set({ zones: v })} allLabel="All zones" /></div>
          <div className="uh-fld"><label htmlFor="v2-cur">Current season <span className="optl">(sets the time windows below)</span></label>
            <select id="v2-cur" className="uh-inp" value={f.currentSeason || engines?.current_season || ''} onChange={(e) => set({ currentSeason: e.target.value })}>
              {seasonOptions.map((o) => <option key={o.value} value={o.value}>{o.label}{o.value === engines?.current_season ? ' (today)' : ''}</option>)}</select></div>
          {footwearish && (
            <div className="uh-fld"><label htmlFor="v2-seasons">Seasons to include <span className="optl">(blank = the default window)</span></label>
              <MultiSelect id="v2-seasons" noun="seasons" options={seasonOptions} value={f.seasons} onChange={(v) => set({ seasons: v })} allLabel="Default window" /></div>
          )}
          <div className="uh-fld"><label htmlFor="v2-cat">Show only these categories <span className="optl">(blank = all)</span></label>
            <CategorySelect id="v2-cat" categories={meta?.categories || []} value={f.categoryIds} onChange={(v) => set({ categoryIds: v })} /></div>
          <div className="uh-fld"><label htmlFor="v2-sku">SKU contains <span className="optl">(blank = all)</span></label>
            <input id="v2-sku" className="uh-inp" value={f.skuSearch} onChange={(e) => set({ skuSearch: e.target.value })} placeholder="e.g. NIKE-DD9473" /></div>
        </div>
        <div className="uh-actions">
          <button type="button" className={`uh-btn primary wide${running ? ' busy' : ''}`} disabled={running} onClick={() => { setPage(1); setView('all'); run(); }}>
            <Ic source={PlayIcon} sm />{running ? 'Building report…' : 'Run V2 report'}</button>
          <span className="uh-help">{HELP[f.strategy]}{engines?.scopes?.[f.strategy] ? ` Covers: ${engines.scopes[f.strategy]}.` : ''}</span>
        </div>
      </div>

      {error && <Alert tone="crit">{error}</Alert>}
      {!result && !running && !error && <div className="uh-card"><div className="uh-empty">Choose a strategy and run the V2 report.</div></div>}
      {result && (
        <div className="uh-card">
          <div className="uh-statcard uh-ir-results"><div className="stats">
            <Stat label="Transfer lines" value={num(s.transfers)} />
            <Stat label="Units to move" value={num(s.units)} />
            {!consolidate && <Stat label="Units to neighbouring regions" value={num(cross)} />}
            {hasLeftovers && <Stat label="Units recommended for the warehouse" value={num(s.leftover_units)} />}
            <Stat label="Retail value" value={money(s.value)} />
          </div></div>
          {result.scope && <div className="uh-help" style={{ padding: '0 16px 8px' }}>Covered: {result.scope}.</div>}
          {hubFull && (
            <div className="uh-ir-hubs">
              {hubFull.map((h) => (
                <div key={h.name} className="hub"><b>{h.name}</b>
                  <small>{num(h.on_hand)} of {num(h.capacity)} footwear units · {num(h.remaining)} room</small>
                  <div className="bar"><i style={{ width: `${Math.min(100, Math.round((h.on_hand / h.capacity) * 100))}%` }} /></div></div>
              ))}
            </div>
          )}
          <div className="uh-ir-tbar">
            <div className="uh-search" style={{ flex: '1 1 260px', maxWidth: 380 }}><Ic source={SearchIcon} sm />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="SKU, product or store" aria-label="Search" /></div>
            {views.map(([k, label]) => <button key={k} type="button" className="uh-ir-pill" aria-pressed={view === k} onClick={() => { setView(k); setPage(1); }}>{label}</button>)}
            <span className="uh-sp" />
            <Pager page={page} setPage={setPage} total={list.length} />
            <button type="button" className="uh-btn" onClick={exportCsv}><Ic source={ExportIcon} sm />Export CSV</button>
          </div>
          <div className="uh-tscroll">
            <table className="uh-tbl uh-ir-tbl">
              <thead>{showingLeftovers
                ? <tr><th>Item</th><th>From</th><th className="r">Qty</th><th>Why</th><th>Feedback</th></tr>
                : <tr><th style={{ width: 34 }}><input type="checkbox" checked={allPageSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
                  <th>Item</th><th>From</th><th>To</th><th className="r">Qty</th><th>Why</th><th>Feedback</th></tr>}</thead>
              <tbody>
                {paged.length === 0 && <tr><td colSpan={7} className="uh-empty">Nothing for this view</td></tr>}
                {showingLeftovers ? paged.map((r, i) => (
                  <tr key={`${r.item_id}:${r.src}:${i}`}>
                    <td><div className="nm">{r.name}</div><div className="sm uh-mono">{r.sku}</div></td>
                    <td><span className="loc">{r.src}</span><div className="sm">{r.src_zone} · {r.src_region}</div></td>
                    <td className="r uh-num">{r.qty}</td>
                    <td><StatusPill tone="hold">Recommend: warehouse / clearance</StatusPill> <span className="uh-muted">{r.why}</span></td>
                    <td><Vote r={r} votes={votes} onVote={(row, verdict) => setVoting({ r: row, verdict })} /></td>
                  </tr>
                )) : paged.map((r) => {
                  const k = rowKey(r);
                  return (
                    <tr key={`${k}:${r.pass}`}>
                      <td><input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} aria-label={`Select ${r.name}, ${r.src} to ${r.dst}`} /></td>
                      <td><div className="nm">{r.name}</div><div className="sm uh-mono">{r.sku}</div></td>
                      <td><span className="loc">{r.src}</span><div className="sm">{r.src_zone} · {r.src_region}</div></td>
                      <td><span className="loc">{r.dst}</span><div className="sm">{r.dst_region}</div></td>
                      <td className="r uh-num">{r.qty}</td>
                      <td>{passPill(r, consolidate)} <span className="uh-muted">{r.why}</span></td>
                      <td><Vote r={r} votes={votes} onVote={(row, verdict) => setVoting({ r: row, verdict })} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="uh-tfoot"><span>{num(list.length)} lines</span><span className="uh-sp" />
            <span>{showingLeftovers ? 'Leftovers are a list for the regional and store managers to decide on. They are not transfers.' : 'One unit per size gap in footwear; quantities are added up per item and store pair.'}</span></div>
          {voting && <VoteDialog vote={voting} prev={votes[voteKey(voting.r.item_id, voting.r.src_id, voting.r.dst_id)]} onClose={() => setVoting(null)} onSave={saveVote} />}
          <ActionBar chosen={chosen} total={shown.length} allSelected={chosen.length >= shown.length} onClear={() => setSelected(new Set())} onCreate={create}
            onSelectAll={() => setSelected(new Set(shown.map(rowKey)))} />
        </div>
      )}
    </>
  );
}

// ── AI ───────────────────────────────────────────────────────────────────────

const AI_SORTS = {
  net: (a, b) => b.net - a.net, gain: (a, b) => b.gain - a.gain, cost: (a, b) => b.cost - a.cost, qty: (a, b) => b.qty - a.qty,
  item: (a, b) => a.name.localeCompare(b.name), from: (a, b) => a.src.localeCompare(b.src), to: (a, b) => a.dst.localeCompare(b.dst),
};

export function AIPanel({ meta, eng, onCreate }) {
  const { f, set, result, used, running, error, run, selected, setSelected } = eng;
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('net');
  const [page, setPage] = useState(1);
  const list = useMemo(() => {
    if (!result) return [];
    const t = search.trim().toLowerCase();
    return result.moves.filter((r) => !t || `${r.sku} ${r.name} ${r.src} ${r.dst}`.toLowerCase().includes(t)).sort(AI_SORTS[sort]);
  }, [result, search, sort]);
  const paged = list.slice((page - 1) * PAGE, page * PAGE);
  const chosen = result ? result.moves.filter((r) => selected.has(rowKey(r))) : [];
  const toggle = (k) => setSelected((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allPageSelected = paged.length > 0 && paged.every((r) => selected.has(rowKey(r)));
  const togglePage = () => setSelected((prev) => { const n = new Set(prev); paged.forEach((r) => (allPageSelected ? n.delete(rowKey(r)) : n.add(rowKey(r)))); return n; });
  const create = () => onCreate({
    lines: chosen.map((r) => ({ src: r.src_id, dest: r.dst_id, item_id: r.item_id, qty: r.qty })),
    labels: Object.fromEntries(chosen.map((r) => [r.item_id, { product: r.name, sku: r.sku }])), reportId: result.report_id, onDone: () => setSelected(new Set()),
  });
  const exportCsv = () => downloadCsv('rebalancing-ai.csv', ['Item', 'SKU', 'Category', 'From', 'To', 'Qty', 'Expected extra revenue', 'Cost to move', 'Net', 'Why'],
    result.moves.map((r) => [r.name, r.sku, r.category, r.src, r.dst, r.qty, r.gain, r.cost, r.net, r.why]));
  const s = result?.summary;
  const th = (key, label, cls = '') => <th className={cls}><button type="button" className="uh-ir-th" onClick={() => { setSort(key); setPage(1); }}>{label}<span aria-hidden="true">{sort === key ? '▼' : ''}</span></button></th>;
  const field = (id, label, key, step = 1, max = 1000) => (
    <div className="uh-fld"><label htmlFor={id}>{label}</label>
      <input id={id} className="uh-inp uh-num" type="number" min={0} max={max} step={step} value={f[key]} onChange={(e) => set({ [key]: Number(e.target.value) })} /></div>
  );
  return (
    <>
      <div className="uh-card uh-card-b uh-form-card">
        <div className="uh-note"><Ic source={InfoIcon} sm />
          <div>The AI tab asks one question: which moves add the most revenue once moving the stock is paid for? It uses the last 30, 60 and 90 days of sales (recent weeks count most) and ignores regions and zones.
            Items go from the stores where they sit unsold to the stores that sell out. Small shipments are dropped, and stock moved recently is left alone, so nothing is shipped around the country repeatedly.</div></div>
        <div className="uh-grid2">
          <div className="uh-fld"><label htmlFor="ai-days">Look ahead</label>
            <select id="ai-days" className="uh-inp" value={f.days} onChange={(e) => set({ days: Number(e.target.value) })}>
              <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option></select></div>
          {field('ai-pay', 'Revenue wanted back for each $1 of shipping', 'payback', 0.5, 20)}
          {field('ai-transfer', 'Cost of one shipment between two stores ($)', 'perTransfer', 1)}
          {field('ai-unit', 'Handling cost per unit ($)', 'perUnit', 0.25, 100)}
          {field('ai-mile', 'Freight per unit per 100 miles ($)', 'perUnit100mi', 0.25, 100)}
          {field('ai-cool', 'Leave stock alone that was moved in the last (days)', 'cooldownDays', 1, 365)}
          <div className="uh-fld"><label htmlFor="ai-cat">Show only these categories <span className="optl">(blank = all)</span></label>
            <CategorySelect id="ai-cat" categories={meta?.categories || []} value={f.categoryIds} onChange={(v) => set({ categoryIds: v })} /></div>
          <div className="uh-fld"><label htmlFor="ai-sku">SKU contains <span className="optl">(blank = all)</span></label>
            <input id="ai-sku" className="uh-inp" value={f.skuSearch} onChange={(e) => set({ skuSearch: e.target.value })} /></div>
        </div>
        <div className="uh-actions">
          <button type="button" className={`uh-btn primary wide${running ? ' busy' : ''}`} disabled={running} onClick={() => { setPage(1); run(); }}>
            <Ic source={PlayIcon} sm />{running ? 'Working it out (about 30 seconds)…' : 'Run AI report'}</button>
          <span className="uh-help">Dollars are revenue at list price. Shipping figures are estimates you can change.</span>
        </div>
      </div>

      {error && <Alert tone="crit">{error}</Alert>}
      {!result && !running && !error && <div className="uh-card"><div className="uh-empty">Run the AI report to see the moves it would make.</div></div>}
      {result && (
        <div className="uh-card">
          <div className="uh-statcard uh-ir-results"><div className="stats">
            <Stat label="Transfer lines" value={num(s.transfers)} />
            <Stat label="Units to move" value={num(s.units)} />
            <Stat label={`Expected extra revenue, ${used.days} days`} value={money(s.gain)} />
            <Stat label="Estimated cost of moving" value={money(s.cost)} />
            <Stat label="Expected net" value={money(s.net)} />
            <Stat label="Shipments" value={num(s.shipments)} />
          </div></div>
          <div className="uh-ir-chips">
            <span className="chip">{num(s.skipped_shipments)} small shipments left out because the gain did not cover the cost</span>
            {s.cooldown_items > 0 && <span className="chip">{num(s.cooldown_items)} items left alone (moved in the last {used.cooldownDays} days)</span>}
            <span className="chip">Needs {used.payback}× back on the shipping</span>
          </div>
          <div className="uh-ir-tbar">
            <div className="uh-search" style={{ flex: '1 1 260px', maxWidth: 380 }}><Ic source={SearchIcon} sm />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="SKU, product or store" aria-label="Search" /></div>
            <span className="uh-sp" />
            <Pager page={page} setPage={setPage} total={list.length} />
            <button type="button" className="uh-btn" onClick={exportCsv}><Ic source={ExportIcon} sm />Export CSV</button>
          </div>
          <div className="uh-tscroll">
            <table className="uh-tbl uh-ir-tbl">
              <thead><tr><th style={{ width: 34 }}><input type="checkbox" checked={allPageSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
                {th('item', 'Item')}{th('from', 'From')}{th('to', 'To')}{th('qty', 'Qty', 'r')}{th('gain', 'Gain', 'r')}{th('cost', 'Cost', 'r')}{th('net', 'Net', 'r')}<th>Why</th></tr></thead>
              <tbody>
                {paged.length === 0 && <tr><td colSpan={9} className="uh-empty">Nothing matches</td></tr>}
                {paged.map((r) => {
                  const k = rowKey(r);
                  return (
                    <tr key={k}>
                      <td><input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} aria-label={`Select ${r.name}, ${r.src} to ${r.dst}`} /></td>
                      <td><div className="nm">{r.name}</div><div className="sm uh-mono">{r.sku}</div></td>
                      <td><span className="loc">{r.src}</span></td><td><span className="loc">{r.dst}</span></td>
                      <td className="r uh-num">{r.qty}</td><td className="r uh-num">{money(r.gain)}</td><td className="r uh-num">{money(r.cost)}</td>
                      <td className="r uh-num"><b className="uh-ir-good">{money(r.net)}</b></td>
                      <td className="uh-muted">{r.why}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="uh-tfoot"><span>{num(list.length)} lines. Gain is expected extra revenue; real results are checked against sales after 30 and 60 days.</span></div>
          <ActionBar chosen={chosen} total={list.length} allSelected={chosen.length >= list.length} onClear={() => setSelected(new Set())} onCreate={create}
            onSelectAll={() => setSelected(new Set(list.map(rowKey)))} />
        </div>
      )}
    </>
  );
}

// ── Compare ──────────────────────────────────────────────────────────────────

export function ComparePanel({ v1, v2, ai, runAll, running }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const sets = useMemo(() => {
    const m = new Map();
    const add = (idx, k, row, qty) => { const e = m.get(k) || { k, row, q: [0, 0, 0], has: [false, false, false] }; e.q[idx] += qty; e.has[idx] = true; if (!e.row) e.row = row; m.set(k, e); };
    (v1?.transfers || []).forEach((t) => add(0, `${t.item_id}:${t.src}:${t.dest}`, { name: t.product, sku: t.sku, src: t.src_name, dst: t.dest_name }, t.move_qty));
    (v2?.moves || []).forEach((r) => add(1, `${r.item_id}:${r.src_id}:${r.dst_id}`, { name: r.name, sku: r.sku, src: r.src, dst: r.dst }, r.qty));
    (ai?.moves || []).forEach((r) => add(2, `${r.item_id}:${r.src_id}:${r.dst_id}`, { name: r.name, sku: r.sku, src: r.src, dst: r.dst }, r.qty));
    return [...m.values()];
  }, [v1, v2, ai]);
  const aiNet = useMemo(() => new Map((ai?.moves || []).map((r) => [`${r.item_id}:${r.src_id}:${r.dst_id}`, r.net])), [ai]);
  const have = [!!v1, !!v2, !!ai];
  const units = (i) => sets.reduce((n, e) => n + e.q[i], 0);
  const overlap = (a, b) => sets.filter((e) => e.has[a] && e.has[b]).reduce((n, e) => n + Math.min(e.q[a], e.q[b]), 0);
  const all3 = sets.filter((e) => e.has[0] && e.has[1] && e.has[2]).reduce((n, e) => n + Math.min(...e.q), 0);
  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    const count = (e) => e.has.filter(Boolean).length;
    return sets.filter((e) => (!t || `${e.row.sku} ${e.row.name} ${e.row.src} ${e.row.dst}`.toLowerCase().includes(t))
      && (filter === 'all' || (filter === '3' ? count(e) === 3 : filter === '2' ? count(e) === 2 : count(e) === 1))).sort((a, b) => b.has.filter(Boolean).length - a.has.filter(Boolean).length || (b.q[2] - a.q[2]));
  }, [sets, filter, search]);
  const paged = rows.slice((page - 1) * PAGE, page * PAGE);
  const names = ['V1', 'V2', 'AI'];
  // Every row the current filter and search show (not just this page): who suggested it and how many units each tab wants.
  const exportCsv = () => downloadCsv('rebalancing-compare.csv', ['Suggested by', 'Tabs agreeing', 'Item', 'SKU', 'From', 'To', 'V1 qty', 'V2 qty', 'AI qty', 'AI net'],
    rows.map((e) => [e.has.map((h, i) => (h ? names[i] : null)).filter(Boolean).join(' + '), e.has.filter(Boolean).length, e.row.name, e.row.sku, e.row.src, e.row.dst,
      ...e.q.map((q, i) => (e.has[i] ? q : '')), aiNet.has(e.k) ? aiNet.get(e.k) : '']));
  return (
    <>
      <div className="uh-card uh-card-b uh-form-card">
        <div className="uh-note"><Ic source={InfoIcon} sm />
          <div>Compares the last report of each tab, in units. A move matches when the item, source and destination are the same. V2 covers footwear and jerseys only, so V1 and AI moves in other categories show as only one.
            Each tab’s suggestions are saved with the date, so after 30 and 60 days the real sales at the receiving stores can be checked against what each tab suggested.</div></div>
        <div className="uh-actions">
          <button type="button" className={`uh-btn primary wide${running ? ' busy' : ''}`} disabled={running} onClick={runAll}><Ic source={PlayIcon} sm />{running ? 'Running all three…' : 'Run all three with their current settings'}</button>
          <span className="uh-help">{have.map((h, i) => `${names[i]} ${h ? 'ready' : 'not run yet'}`).join(' · ')}</span>
        </div>
      </div>
      {have.some(Boolean) && (
        <div className="uh-card">
          <div className="uh-statcard uh-ir-results"><div className="stats">
            <Stat label="V1 units to move" value={have[0] ? num(units(0)) : '–'} />
            <Stat label="V2 units to move" value={have[1] ? num(units(1)) : '–'} />
            <Stat label="AI units to move" value={have[2] ? num(units(2)) : '–'} />
            <Stat label="Units all three agree on" value={have.every(Boolean) ? num(all3) : '–'} />
            <Stat label="V1 and V2 agree" value={have[0] && have[1] ? num(overlap(0, 1)) : '–'} />
            <Stat label="V1 and AI agree" value={have[0] && have[2] ? num(overlap(0, 2)) : '–'} />
            <Stat label="V2 and AI agree" value={have[1] && have[2] ? num(overlap(1, 2)) : '–'} />
            <Stat label="AI expected net" value={have[2] ? money(ai.summary.net) : '–'} />
          </div></div>
          <div className="uh-ir-tbar">
            <div className="uh-search" style={{ flex: '1 1 260px', maxWidth: 380 }}><Ic source={SearchIcon} sm />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="SKU, product or store" aria-label="Search" /></div>
            {[['all', 'All'], ['3', 'All three'], ['2', 'Two of three'], ['1', 'Only one']].map(([k, label]) => (
              <button key={k} type="button" className="uh-ir-pill" aria-pressed={filter === k} onClick={() => { setFilter(k); setPage(1); }}>{label}</button>))}
            <span className="uh-sp" /><Pager page={page} setPage={setPage} total={rows.length} />
            <button type="button" className="uh-btn" onClick={exportCsv} disabled={!rows.length}><Ic source={ExportIcon} sm />Export CSV</button>
          </div>
          <div className="uh-tscroll">
            <table className="uh-tbl uh-ir-tbl">
              <thead><tr><th>Suggested by</th><th>Item</th><th>From</th><th>To</th><th className="r">V1</th><th className="r">V2</th><th className="r">AI</th><th className="r">AI net</th></tr></thead>
              <tbody>
                {paged.length === 0 && <tr><td colSpan={8} className="uh-empty">Nothing for this filter</td></tr>}
                {paged.map((e) => (
                  <tr key={e.k}>
                    <td>{e.has.map((h, i) => (h ? <StatusPill key={i} tone={i === 2 ? 'hold' : i === 1 ? 'ful' : 'neu'}>{names[i]}</StatusPill> : null))}</td>
                    <td><div className="nm">{e.row.name}</div><div className="sm uh-mono">{e.row.sku}</div></td>
                    <td><span className="loc">{e.row.src}</span></td><td><span className="loc">{e.row.dst}</span></td>
                    {e.q.map((q, i) => <td key={i} className="r uh-num">{e.has[i] ? q : '–'}</td>)}
                    <td className="r uh-num">{aiNet.has(e.k) ? money(aiNet.get(e.k)) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="uh-tfoot"><span>{num(rows.length)} distinct moves across the three tabs.</span></div>
        </div>
      )}
    </>
  );
}
